import { APIMain } from "@/apiRequester";
import { ApiConstant } from "@/const";
import { IBankRow } from "@/features/banks/types/bank.type";
import { putSysTableAction } from "@/actions/systable-action";
import {
  IUpdateWebUserPayload,
  IWebUserDetail,
} from "@/features/members/types/member.type";
import {
  IProfileCreateMemberContext,
  IProfileCreateMemberPayload,
  IProfileDashboard,
  IProfileGeneralSystemSetting,
  IProfileGenerationMembersPage,
  IProfilePagingResult,
  IProfilePurchasePackageContext,
  IProfilePurchasePackagePayload,
  IProfilePurchasePackageResult,
  IProfileProductPurchaseContext,
  IProfileProductPurchaseItem,
  IProfileProductPurchasePayload,
  IProfileProductPurchaseResult,
  IProfileStructureMembersPage,
  IProfileRewardHistoryItem,
  IProfileWithdrawalHistoryItem,
  IProfileWithdrawPointsContext,
  IProfileWithdrawPointsResult,
  IWithdrawPointsPayload,
  ICreateSepayTopupOrderPayload,
  ISepayTopupOrder,
  IProvinceOption,
  ICommuneOption,
  ITopupComboOption,
} from "../types/profile.type";

const getDashboard = async () => {
  return APIMain.get<IProfileDashboard>(ApiConstant.PROFILE_API.GET_DASHBOARD);
};

const getGeneralSettings = async () => {
  return APIMain.get<IProfileGeneralSystemSetting>(
    ApiConstant.PROFILE_API.GET_GENERAL_SETTINGS,
  );
};

const getPurchasePackageContext = async () => {
  return APIMain.get<IProfilePurchasePackageContext>(
    ApiConstant.PROFILE_API.GET_PURCHASE_PACKAGE_CONTEXT,
  );
};

const purchasePackage = async (payload: IProfilePurchasePackagePayload) => {
  const res = await APIMain.post<IProfilePurchasePackageResult>(
    ApiConstant.PROFILE_API.POST_PURCHASE_PACKAGE,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      createdAtUtc: new Date(res.payload.data.createdAtUtc),
    };
  }

  return res;
};

const getEditInfo = async () => {
  return APIMain.get<IWebUserDetail>(ApiConstant.PROFILE_API.GET_EDIT_INFO);
};

const getBankOptions = async () => {
  return APIMain.get<IBankRow[]>(ApiConstant.PROFILE_API.GET_BANK_OPTIONS);
};

const getProvinceOptions = async () => {
  return APIMain.get<IProvinceOption[]>(
    ApiConstant.PROFILE_API.GET_PROVINCE_OPTIONS,
  );
};

const getCommuneOptions = async (provinceCode: string) => {
  return APIMain.get<ICommuneOption[]>(
    ApiConstant.PROFILE_API.GET_COMMUNE_OPTIONS,
    { provinceCode },
  );
};

const updateEditInfo = async (payload: IUpdateWebUserPayload) => {
  return putSysTableAction(payload, ApiConstant.PROFILE_API.PUT_EDIT_INFO);
};

const getWithdrawPointsContext = async () => {
  const res = await APIMain.get<IProfileWithdrawPointsContext>(
    ApiConstant.PROFILE_API.GET_WITHDRAW_POINTS_CONTEXT,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      today: new Date(res.payload.data.today),
      firstSundayOfMonth: new Date(res.payload.data.firstSundayOfMonth),
      thirdSundayOfMonth: new Date(res.payload.data.thirdSundayOfMonth),
      nextOpenDate: new Date(res.payload.data.nextOpenDate),
    };
  }

  return res;
};

const withdrawPoints = async (payload: IWithdrawPointsPayload) => {
  const res = await APIMain.post<IProfileWithdrawPointsResult>(
    ApiConstant.PROFILE_API.POST_WITHDRAW_POINTS,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      createdAtUtc: new Date(res.payload.data.createdAtUtc),
    };
  }

  return res;
};

const getRewardHistory = async (params: { skip: number; take: number }) => {
  const res = await APIMain.get<IProfilePagingResult<IProfileRewardHistoryItem>>(
    ApiConstant.PROFILE_API.GET_REWARD_HISTORY,
    params,
  );

  if (res.payload?.data?.items) {
    res.payload.data.items = res.payload.data.items.map((item) => ({
      ...item,
      createdAtUtc: new Date(item.createdAtUtc),
    }));
  }

  return res;
};

const getWithdrawalHistory = async (params: { skip: number; take: number }) => {
  const res = await APIMain.get<
    IProfilePagingResult<IProfileWithdrawalHistoryItem>
  >(ApiConstant.PROFILE_API.GET_WITHDRAWAL_HISTORY, params);

  if (res.payload?.data?.items) {
    res.payload.data.items = res.payload.data.items.map((item) => ({
      ...item,
      createdAtUtc: new Date(item.createdAtUtc),
    }));
  }

  return res;
};

const getStructureMembers = async (params: {
  branch: "left" | "right";
  keyword?: string;
  page: number;
  pageSize: number;
}) => {
  const res = await APIMain.get<IProfileStructureMembersPage>(
    ApiConstant.PROFILE_API.GET_STRUCTURE_MEMBERS,
    params,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      members: (res.payload.data.members || []).map((item) => ({
          ...item,
          createdAtUtc: new Date(item.createdAtUtc),
        })),
    };
  }

  return res;
};

const getGenerationMembers = async (params: {
  keyword?: string;
  page: number;
  pageSize: number;
}) => {
  const res = await APIMain.get<IProfileGenerationMembersPage>(
    ApiConstant.PROFILE_API.GET_GENERATION_MEMBERS,
    params,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      members: (res.payload.data.members || []).map((item) => ({
        ...item,
        createdAtUtc: new Date(item.createdAtUtc),
      })),
    };
  }

  return res;
};

const getCreateMemberContext = async () => {
  return APIMain.get<IProfileCreateMemberContext>(
    ApiConstant.PROFILE_API.GET_CREATE_MEMBER_CONTEXT,
  );
};

const createMember = async (payload: IProfileCreateMemberPayload) => {
  return APIMain.post<IWebUserDetail>(
    ApiConstant.PROFILE_API.POST_CREATE_MEMBER,
    payload,
  );
};

const getProductPurchaseContext = async () => {
  const res = await APIMain.get<IProfileProductPurchaseContext>(
    ApiConstant.PROFILE_API.GET_PRODUCT_PURCHASE_CONTEXT,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      today: new Date(res.payload.data.today),
    };
  }

  return res;
};

const purchaseProduct = async (payload: IProfileProductPurchasePayload) => {
  const res = await APIMain.post<IProfileProductPurchaseResult>(
    ApiConstant.PROFILE_API.POST_PRODUCT_PURCHASE,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = {
      ...res.payload.data,
      createdAtUtc: new Date(res.payload.data.createdAtUtc),
    };
  }

  return res;
};

const getProductPurchases = async (params: { skip: number; take: number }) => {
  const res = await APIMain.get<IProfilePagingResult<IProfileProductPurchaseItem>>(
    ApiConstant.PROFILE_API.GET_PRODUCT_PURCHASES,
    params,
  );

  if (res.payload?.data?.items) {
    res.payload.data.items = res.payload.data.items.map((item) => ({
      ...item,
      createdAtUtc: new Date(item.createdAtUtc),
      reviewedAtUtc: item.reviewedAtUtc ? new Date(item.reviewedAtUtc) : null,
    }));
  }

  return res;
};

const getTopupCombos = async () => {
  return APIMain.get<ITopupComboOption[]>(
    ApiConstant.PROFILE_API.GET_TOPUP_COMBOS,
  );
};

const mapSepayTopupOrder = (order: ISepayTopupOrder): ISepayTopupOrder => ({
  ...order,
  createdAtUtc: new Date(order.createdAtUtc),
  expiredAtUtc: new Date(order.expiredAtUtc),
  completedAtUtc: order.completedAtUtc ? new Date(order.completedAtUtc) : null,
});

const createSepayTopupOrder = async (
  payload: ICreateSepayTopupOrderPayload,
) => {
  const res = await APIMain.post<ISepayTopupOrder>(
    ApiConstant.PROFILE_API.POST_TOPUP_ORDER,
    payload,
  );

  if (res.payload?.data) {
    res.payload.data = mapSepayTopupOrder(res.payload.data);
  }

  return res;
};

const getSepayTopupOrder = async (orderId: string) => {
  const res = await APIMain.get<ISepayTopupOrder>(
    `${ApiConstant.PROFILE_API.GET_TOPUP_ORDER}/${orderId}`,
    undefined,
    { skipLoading: true },
  );

  if (res.payload?.data) {
    res.payload.data = mapSepayTopupOrder(res.payload.data);
  }

  return res;
};

export const profileServices = {
  getDashboard,
  getGeneralSettings,
  getPurchasePackageContext,
  purchasePackage,
  getEditInfo,
  getBankOptions,
  getProvinceOptions,
  getCommuneOptions,
  updateEditInfo,
  getWithdrawPointsContext,
  withdrawPoints,
  getWithdrawalHistory,
  getRewardHistory,
  getStructureMembers,
  getGenerationMembers,
  getCreateMemberContext,
  createMember,
  getProductPurchaseContext,
  purchaseProduct,
  getProductPurchases,
  getTopupCombos,
  createSepayTopupOrder,
  getSepayTopupOrder,
};
